/**
 * W1-11a (app-registry-keymap): reserved and unreliable chords with their explanations (DESIGN.md §8.1).
 *
 * Re-exports the contracted lists and explains each entry with a translatable reason, so the keymap recorder
 * (W2-13) can refuse a chord with "Your browser keeps Ctrl+1 for switching tabs; pick another key." and warn about
 * unreliable ones. `checkChord` is the single rule the recorder, the JSON import and the preset audit apply:
 *   - refused: invalid chords, RESERVED_CHORDS (every OS), RESERVED_CHORDS_MAC (on macOS), Ctrl+Alt (AltGr);
 *   - warned: UNRELIABLE_CHORDS;
 *   - accepted: everything else.
 * `hold:` chords are checked by their trigger; wheel and `char:` chords are never reserved.
 */

import type { MessageRef } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import { formatChord, normalizeChord, parseChord, triggerOf, type Os } from './chords';
import { m } from './registry.messages';
import { ref } from './text';
import { RESERVED_CHORDS, RESERVED_CHORDS_MAC, UNRELIABLE_CHORDS, type Chord } from './types';

export { RESERVED_CHORDS, RESERVED_CHORDS_MAC, UNRELIABLE_CHORDS };

/** Reason keys for RESERVED_CHORDS (every OS). */
const RESERVED_REASONS: ReadonlyMap<Chord, MessageKey> = (() => {
  const map = new Map<Chord, MessageKey>();
  const set = (chords: readonly Chord[], key: MessageKey): void => {
    for (const c of chords) map.set(c, key);
  };
  set(
    [
      'Ctrl+KeyT',
      'Ctrl+KeyN',
      'Ctrl+KeyW',
      'Ctrl+KeyQ',
      'Ctrl+Shift+KeyT',
      'Ctrl+Shift+KeyN',
      'Ctrl+Shift+KeyW',
      'Ctrl+Shift+KeyQ',
    ],
    m.reservedTabsWindows,
  );
  set(['Ctrl+Tab', 'Ctrl+Shift+Tab', 'Ctrl+PageUp', 'Ctrl+PageDown'], m.reservedTabSwitch);
  for (let d = 1; d <= 9; d++) {
    map.set(`Ctrl+Digit${String(d)}`, m.reservedTabSwitch);
    map.set(`Alt+Digit${String(d)}`, m.reservedTabSwitch);
  }
  set(['Alt+ArrowLeft', 'Alt+ArrowRight', 'Alt+Home'], m.reservedHistory);
  set(['Alt+F4'], m.reservedCloseWindow);
  set(['F11'], m.reservedFullscreen);
  set(
    ['F12', 'Ctrl+Shift+KeyI', 'Ctrl+Shift+KeyJ', 'Ctrl+Shift+KeyC', 'Ctrl+Shift+KeyK', 'Ctrl+Shift+KeyE'],
    m.reservedDevtools,
  );
  set(['Ctrl+Shift+KeyP'], m.reservedPrivate);
  set(['Ctrl+Shift+KeyA'], m.reservedAddons);
  set(['Ctrl+Shift+KeyM'], m.reservedBrowserTool);
  set(['Ctrl+Shift+KeyS'], m.reservedScreenshot);
  set(['Ctrl+KeyR', 'Ctrl+Shift+KeyR'], m.reservedReload);
  set(['Ctrl+Shift+Delete'], m.reservedClearData);
  return map;
})();

/** Reason keys for RESERVED_CHORDS_MAC (`Ctrl` = Cmd). */
const RESERVED_MAC_REASONS: ReadonlyMap<Chord, MessageKey> = new Map<Chord, MessageKey>([
  ['Ctrl+KeyQ', m.reservedMacApp],
  ['Ctrl+KeyW', m.reservedMacApp],
  ['Ctrl+KeyT', m.reservedMacApp],
  ['Ctrl+KeyN', m.reservedMacApp],
  ['Ctrl+KeyH', m.reservedMacWindow],
  ['Ctrl+KeyM', m.reservedMacWindow],
  ['Ctrl+Comma', m.reservedMacPrefs],
  ['Ctrl+Backquote', m.reservedMacCycle],
  ['Ctrl+BracketLeft', m.reservedMacHistory],
  ['Ctrl+BracketRight', m.reservedMacHistory],
  ['Ctrl+ArrowLeft', m.reservedMacHistory],
  ['Ctrl+ArrowRight', m.reservedMacHistory],
  ['Ctrl+KeyL', m.reservedMacAddress],
  ['Ctrl+Shift+KeyQ', m.reservedMacLogout],
  // Proposed additions (change request W1-11a): explained already, refused once the contract lists them.
  ['Ctrl+Shift+Backquote', m.reservedMacCycle],
  ['Ctrl+Shift+BracketLeft', m.reservedMacTabs],
  ['Ctrl+Shift+BracketRight', m.reservedMacTabs],
]);

const UNRELIABLE_REASONS: ReadonlyMap<Chord, MessageKey> = new Map<Chord, MessageKey>([
  ['Alt+KeyD', m.unreliableMenu],
  ['Alt+KeyE', m.unreliableMenu],
  ['Alt+KeyF', m.unreliableMenu],
  ['F6', m.unreliableAddress],
  ['Ctrl+KeyL', m.unreliableAddress],
  ['Ctrl+KeyJ', m.unreliableDownloads],
  ['Ctrl+KeyH', m.unreliableHistory],
]);

const RESERVED = new Set(RESERVED_CHORDS.map((c) => normalizeChord(c) ?? c));
const RESERVED_MAC = new Set(RESERVED_CHORDS_MAC.map((c) => normalizeChord(c) ?? c));
const UNRELIABLE = new Set(UNRELIABLE_CHORDS.map((c) => normalizeChord(c) ?? c));

/** Label function used for the `{chord}` parameter (the keymap passes its layout-aware `label`). */
export type ChordLabel = (chord: Chord) => string;

const plainLabel: ChordLabel = (c) => c;

/** True when the chord (or its trigger, for `hold:`) is reserved on this OS. */
export function isReserved(chord: Chord, os: Os): boolean {
  const t = triggerOf(chord);
  if (t === null) return false;
  return RESERVED.has(t) || (os === 'mac' && RESERVED_MAC.has(t));
}

/** True when the chord is in RESERVED_CHORDS_MAC (whatever the current OS). */
export function isMacReserved(chord: Chord): boolean {
  const t = triggerOf(chord);
  return t !== null && RESERVED_MAC.has(t);
}

export function isUnreliable(chord: Chord): boolean {
  const t = triggerOf(chord);
  return t !== null && UNRELIABLE.has(t);
}

/** Why the recorder refuses this chord on this OS, or null when it is not reserved. */
export function reservedReason(chord: Chord, os: Os, label: ChordLabel = plainLabel): MessageRef | null {
  const t = triggerOf(chord);
  if (t === null) return null;
  if (RESERVED.has(t)) return ref(RESERVED_REASONS.get(t) ?? m.reservedBrowserTool, { chord: label(t) });
  if (os === 'mac' && RESERVED_MAC.has(t))
    return ref(RESERVED_MAC_REASONS.get(t) ?? m.reservedMacOther, { chord: label(t) });
  return null;
}

/** Why a chord may not always reach the page, or null. */
export function unreliableReason(chord: Chord, label: ChordLabel = plainLabel): MessageRef | null {
  const t = triggerOf(chord);
  if (t === null) return null;
  const key = UNRELIABLE_REASONS.get(t);
  return key === undefined ? null : ref(key, { chord: label(t) });
}

export type ChordCheck =
  | { readonly status: 'ok'; readonly chord: Chord }
  | { readonly status: 'unreliable'; readonly chord: Chord; readonly reason: MessageRef }
  | { readonly status: 'reserved' | 'ctrlAlt'; readonly chord: Chord; readonly reason: MessageRef }
  | { readonly status: 'invalid'; readonly chord: string };

/** Whether a chord may be bound: 'ok' / 'unreliable' are accepted, 'reserved' / 'ctrlAlt' / 'invalid' refused. */
export function checkChord(chord: string, os: Os, label: ChordLabel = plainLabel): ChordCheck {
  const p = parseChord(chord);
  if (p === null) return { status: 'invalid', chord };
  const canonical = formatChord(p);
  if (p.kind === 'key') {
    const reason = reservedReason(canonical, os, label);
    if (reason !== null) return { status: 'reserved', chord: canonical, reason };
  }
  if (p.ctrl && p.alt) return { status: 'ctrlAlt', chord: canonical, reason: ref(m.reservedCtrlAlt) };
  const warn = p.kind === 'key' ? unreliableReason(canonical, label) : null;
  if (warn !== null) return { status: 'unreliable', chord: canonical, reason: warn };
  return { status: 'ok', chord: canonical };
}

/** True when checkChord refuses the chord. */
export function isRefused(c: ChordCheck): boolean {
  return c.status === 'reserved' || c.status === 'ctrlAlt' || c.status === 'invalid';
}
